import { ALL_PERMISSIONS, DEFAULT_MEMBER_PERMISSIONS, PERMISSIONS } from '@/lib/permissions';

/**
 * `?mock=full&scale=big`: one server at the size the limits allow and a big
 * community reaches — 250 roles, 1500 members, 80 channels — for timing the
 * member list, settings and channel list, and for checking layouts at
 * volume. Deterministic, so two runs measure the same thing.
 */

export const BIG_GUILD_ID = 'g_big';
export const BIG_ROLES = 250;
export const BIG_MEMBERS = 1500;
const CATEGORIES = 8;
const CHANNELS_PER_CATEGORY = 9;
const STATUSES = ['ONLINE', 'IDLE', 'DND', 'OFFLINE', 'OFFLINE'] as const;
/** The roster page the real server sends with the detail. */
const DETAIL_PAGE = 200;

function pad(n: number, width = 4): string {
  return String(n).padStart(width, '0');
}

export function makeBigServer(now: number) {
  const ts = new Date(now - 60 * 24 * 3600_000).toISOString();
  const roles = [
    {
      id: BIG_GUILD_ID,
      guildId: BIG_GUILD_ID,
      name: '@everyone',
      color: 0,
      position: 0,
      permissions: DEFAULT_MEMBER_PERMISSIONS.toString(),
    },
    ...Array.from({ length: BIG_ROLES - 1 }, (_, i) => ({
      id: `r_big_${pad(i)}`,
      guildId: BIG_GUILD_ID,
      name: i < 10 ? `Модератор ${i + 1}` : `Команда ${pad(i - 9, 3)}`,
      color: (0x224466 + i * 0x010305) & 0xffffff,
      position: BIG_ROLES - 1 - i,
      permissions: (i < 10 ? PERMISSIONS.MANAGE_MESSAGES | PERMISSIONS.KICK_MEMBERS : 0n).toString(),
    })),
  ];

  const members = Array.from({ length: BIG_MEMBERS }, (_, i) => {
    const id = `u_big_${pad(i)}`;
    const roleIds = i % 7 === 0 ? [] : [roles[1 + (i % (BIG_ROLES - 1))]!.id];
    return {
      guildId: BIG_GUILD_ID,
      userId: id,
      nickname: i % 11 === 0 ? `Ник ${i}` : null,
      joinedAt: new Date(now - (i + 1) * 3600_000).toISOString(),
      roleIds,
      timeoutUntil: null,
      user: {
        id,
        platformId: `p_big_${pad(i)}`,
        username: `player${pad(i)}`,
        displayName: `Игрок ${i}`,
        avatarUrl: null,
        status: STATUSES[i % STATUSES.length],
        teamTag: i % 5 === 0 ? `T${i % 40}` : null,
      },
    };
  });

  const channels: Array<Record<string, unknown>> = [];
  let position = 0;
  for (let c = 0; c < CATEGORIES; c += 1) {
    const categoryId = `cat_big_${c}`;
    channels.push({ id: categoryId, guildId: BIG_GUILD_ID, type: 'CATEGORY', name: `Раздел ${c + 1}`, topic: null, position: position++, parentId: null, createdAt: ts, updatedAt: ts });
    for (let k = 0; k < CHANNELS_PER_CATEGORY; k += 1) {
      const voice = k >= CHANNELS_PER_CATEGORY - 3;
      channels.push({
        id: `c_big_${c}_${k}`,
        guildId: BIG_GUILD_ID,
        type: voice ? 'VOICE' : 'TEXT',
        name: voice ? `голос-${c}-${k}` : `чат-${c}-${k}`,
        topic: null,
        position: position++,
        parentId: categoryId,
        slowmodeS: 0,
        bitrateKbps: voice ? 96 : null,
        userLimit: voice ? 0 : null,
        createdAt: ts,
        updatedAt: ts,
      });
    }
  }

  // The mock user owns it: every right.
  const all = ALL_PERMISSIONS.toString();
  const guild = {
    id: BIG_GUILD_ID,
    name: 'Большой сервер',
    iconUrl: null,
    ownerId: 'me_0',
    region: 'ru',
    createdAt: ts,
    updatedAt: ts,
    myPermissions: all,
    channels,
  };

  const roleMemberCounts: Record<string, number> = {};
  for (const member of members) {
    for (const roleId of member.roleIds) roleMemberCounts[roleId] = (roleMemberCounts[roleId] ?? 0) + 1;
  }

  /** Members after `after` (ordered by user id), as /members pages them. */
  function page(after: string | null, limit: number) {
    const start = after ? members.findIndex((m) => m.userId > after) : 0;
    const from = start === -1 ? members.length : start;
    const slice = members.slice(from, from + limit);
    const more = from + limit < members.length;
    return { members: slice, nextCursor: more ? slice[slice.length - 1]!.userId : null };
  }

  function detail() {
    const first = page(null, DETAIL_PAGE);
    return {
      ...guild,
      roles,
      members: first.members,
      membersNextCursor: first.nextCursor,
      memberCount: members.length,
      roleMemberCounts,
      channels: channels.map((channel) => ({ ...channel, overrides: [] })),
    };
  }

  return { guild, detail, page };
}
